import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, Search, ChevronDown, ChevronUp, DollarSign, Building, CreditCard, Download, RefreshCw, RotateCw, Trash2, Pencil, Archive, ArchiveRestore } from 'lucide-react';
import api from './api/axios';
import { formatApiError } from './api/errors';
import Pagination from './components/Pagination';

// '' means none. The two booleans are mutually exclusive server-side.
const accountUseOf = (account) => (account.is_iolta ? 'iolta' : account.is_operating ? 'operating' : '');

// Why the daily low-balance digest skips this operating account, or null when it is evaluated.
const lowBalancePausedReason = (account) => {
  if (account.link_status !== 'Linked') return account.link_status === 'Broken' ? 'link broken' : 'link not active';
  if (account.hidden_at) return 'hidden';
  if (account.dismissed_at) return 'dismissed';
  if (account.removed_at) return 'removed';
  return null;
};

const AccountSummary = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lawFirmAccounts, setLawFirmAccounts] = useState([]);
  const [expandedFirms, setExpandedFirms] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  // Nickname and account use (IOLTA / Operating / none) are edited together in one
  // explicit Save/Cancel form, keyed by account_id. Both drafts are local until Save, so
  // Cancel is an honest discard - account use drives alert thresholds and Balance
  // Analytics categorization, and must never be written by merely changing the dropdown.
  const [editingAccount, setEditingAccount] = useState(null);
  const [nicknameDraft, setNicknameDraft] = useState('');
  const [accountUseDraft, setAccountUseDraft] = useState('');
  // String so a blank input means "no override" rather than 0.
  const [thresholdDraft, setThresholdDraft] = useState('');
  // type="number" reports "" for unparseable text (e.g. "5,000"), which would otherwise
  // read as blank and clear the override.
  const [thresholdBadInput, setThresholdBadInput] = useState(false);
  const [savingAccountId, setSavingAccountId] = useState(null);
  const [savedAccountId, setSavedAccountId] = useState(null);
  const [downloadingReport, setDownloadingReport] = useState({});
  const [dismissingAccount, setDismissingAccount] = useState(null);
  const [hidingAccount, setHidingAccount] = useState(null);
  const [hideConfirmAccount, setHideConfirmAccount] = useState(null);
  const [showHidden, setShowHidden] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const searchDebounceRef = useRef(null);
  const savedIndicatorRef = useRef(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    total_records: 0,
    total_pages: 1,
    current_page: 1,
    page_size: 10,
    has_next: false,
    has_previous: false
  });
  
  // Summary stats
  const [stats, setStats] = useState({
    totalLawFirms: 0,
    totalLinkedAccounts: 0,
    totalBalance: 0
  });

  // Toggle expansion for a specific law firm
  const toggleExpand = (lawFirmName) => {
    setExpandedFirms(prev => ({
      ...prev,
      [lawFirmName]: !prev[lawFirmName]
    }));
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format the account-removal timestamp for the badge tooltip
  const formatRemovedAt = (value) => {
    if (!value) return 'an unknown date';
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? 'an unknown date' : parsed.toLocaleDateString();
  };

  // The account's last day with recorded transaction activity ("YYYY-MM-DD" from the
  // API, or null when the account has no recorded activity at all). Stale dates next
  // to a sibling's recent ones are the main signal that an account is really dead.
  const formatLastActivity = (value) => {
    if (!value) return '—';
    const parsed = new Date(`${value}T00:00:00`);
    return isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString();
  };

  // Edit, Hide/Unhide and Dismiss all mutate the same row, and the server has no
  // cross-endpoint locking. Mutual exclusion in the UI is what keeps a Dismiss from
  // racing an in-flight Hide (which can leave an account both hidden and dismissed,
  // stranding it) and what keeps an edit session from opening against stale data.
  const isRowBusy = (accountId) =>
    savingAccountId === accountId ||
    hidingAccount === accountId ||
    dismissingAccount === accountId;

  const startEditingAccount = (account) => {
    setEditingAccount(account.account_id);
    setNicknameDraft(account.nickname || '');
    setAccountUseDraft(accountUseOf(account));
    setThresholdDraft(account.low_balance_threshold ?? '');
    setThresholdBadInput(false);
  };

  const cancelEditingAccount = () => {
    setEditingAccount(null);
    setNicknameDraft('');
    setAccountUseDraft('');
    setThresholdDraft('');
    setThresholdBadInput(false);
  };

  // Enter saves and Escape cancels, from every control in the edit row.
  const handleEditKeyDown = (account) => (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveAccountEdits(account);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEditingAccount();
    }
  };

  const showSavedIndicator = (accountId) => {
    setSavedAccountId(accountId);
    if (savedIndicatorRef.current) {
      clearTimeout(savedIndicatorRef.current);
    }
    savedIndicatorRef.current = setTimeout(() => {
      setSavedAccountId(null);
      savedIndicatorRef.current = null;
    }, 1800);
  };

  // Save nickname and account use together, but only PATCH the fields that actually
  // changed: an unchanged account-use write still logs server-side and
  // would clobber a concurrent edit by another staff member. Saving an empty nickname
  // is the only way to clear one: the server normalizes blank input back to null and
  // returns the stored value, so state is written from the response rather than from
  // the raw input. Edit mode stays open on failure so the user's draft survives.
  const saveAccountEdits = async (account) => {
    const accountId = account.account_id;
    const nickname = nicknameDraft;
    const accountUse = accountUseDraft;
    const isOperating = accountUse === 'operating';
    const nicknameChanged = nickname !== (account.nickname || '');
    const accountUseChanged = accountUse !== accountUseOf(account);

    // The override is only ever written while the account use draft is Operating.
    const thresholdText = String(thresholdDraft).trim();
    const thresholdValue = thresholdText === '' ? null : Number(thresholdText);
    if (isOperating && (thresholdBadInput || (thresholdValue !== null && (!Number.isFinite(thresholdValue) || thresholdValue < 0)))) {
      alert('Low-balance threshold must be a number of 0 or more, or blank to use the subscription default.');
      return;
    }
    const thresholdChanged = isOperating && thresholdValue !== (account.low_balance_threshold ?? null);

    if (!nicknameChanged && !accountUseChanged && !thresholdChanged) {
      setEditingAccount(prev => (prev === accountId ? null : prev));
      showSavedIndicator(accountId);
      return;
    }

    const patches = [];
    if (nicknameChanged) {
      patches.push(
        api.patch(`/api/accounts/${accountId}/nickname`, { nickname })
          .then(response => ({ field: 'nickname', data: response.data }))
      );
    }
    if (accountUseChanged) {
      patches.push(
        api.patch(`/api/accounts/${accountId}/account-use`, { account_use: accountUse || null })
          .then(response => ({ field: 'account_use', data: response.data }))
      );
    }

    setSavingAccountId(accountId);
    try {
      const results = await Promise.all(patches);
      const changes = {};
      // Sequenced after the account-use write: the server rejects a threshold on a non-operating account.
      if (thresholdChanged) {
        const response = await api.patch('/api/accounts/low-balance-threshold', {
          account_ids: [accountId],
          threshold: thresholdValue
        });
        const row = response.data.updated.find(u => u.account_id === accountId);
        changes.low_balance_threshold = row.low_balance_threshold;
        changes.low_balance_threshold_updated_at = row.low_balance_threshold_updated_at;
        changes.low_balance_threshold_updated_by = row.low_balance_threshold_updated_by;
      }
      results.forEach(result => {
        if (result.field === 'nickname') {
          changes.nickname = result.data?.nickname ?? null;
        } else if (result.field === 'account_use') {
          changes.is_iolta = result.data.is_iolta;
          changes.is_operating = result.data.is_operating;
        }
      });

      setLawFirmAccounts(prevAccounts =>
        prevAccounts.map(lawFirm => ({
          ...lawFirm,
          accounts: lawFirm.accounts.map(existing =>
            existing.account_id === accountId
              ? { ...existing, ...changes }
              : existing
          )
        }))
      );

      // Guarded so a save that lands after the user moved on doesn't close the row
      // they are now editing.
      setEditingAccount(prev => (prev === accountId ? null : prev));

      showSavedIndicator(accountId);
    } catch (error) {
      console.error('Error saving account changes:', error);
      // When both patches are in flight, a rejection says nothing about whether the
      // other one landed, and there is no server-side rollback. Refetch so the row
      // shows what actually persisted; edit mode and the draft are left untouched.
      fetchAccountData(currentPage, pageSize, searchTerm, showHidden);
      const detail = formatApiError(error);
      alert(`Failed to save changes for "${account.name}".${detail ? ` ${detail}` : ''} Please try again.`);
    } finally {
      setSavingAccountId(prev => (prev === accountId ? null : prev));
    }
  };

  // Permanently remove an account that was removed at the bank. Soft delete on the
  // server (dismissed_at) - transaction history is kept, but the account disappears
  // from this dashboard and from the Excel report. There is no un-dismiss in the UI.
  // Deliberately worded "remove", not "hide", in all user-facing copy below -- the
  // Hide/Unhide feature owns that word for a different, reversible action.
  const dismissAccount = async (accountId, accountName) => {
    const confirmed = window.confirm(
      `Dismiss "${accountName}"?\n\n` +
      'This account will be permanently removed from the dashboard, and its ' +
      "transactions will no longer appear in this law firm's Excel transaction " +
      'report. Its history stays in the database, but this cannot be undone ' +
      'from the dashboard.'
    );
    if (!confirmed) {
      return;
    }

    setDismissingAccount(accountId);
    try {
      await api.post(`/api/accounts/${accountId}/dismiss`);
      // Refetch rather than patching local state: the account leaves the server-side
      // grouping entirely, which also changes the firm's totalAccounts/removedAccounts.
      await fetchAccountData(currentPage, pageSize, searchTerm, showHidden);
    } catch (error) {
      console.error('Error dismissing account:', error);
      alert('Failed to dismiss account. Please try again.');
    } finally {
      setDismissingAccount(null);
    }
  };

  // Set aside an account that is still live at the bank (a duplicate, a closed-out
  // matter, a test link). Reversible: the row comes back with the "Show hidden
  // accounts" toggle and can be unhidden from there. Hiding silently stops
  // balance-threshold alert monitoring for the account, so hiding is gated behind
  // a confirmation dialog (see hideConfirmAccount) before this function is called.
  const setAccountHidden = async (accountId, accountName, hidden) => {
    setHidingAccount(accountId);
    try {
      await api.patch(`/api/accounts/${accountId}/hidden`, { hidden });
      // Refetch for the same reason dismissing does: the firm's totalAccounts,
      // totalBalance and hiddenAccounts all change, and the row itself leaves the
      // default view.
      await fetchAccountData(currentPage, pageSize, searchTerm, showHidden);
    } catch (error) {
      console.error('Error updating account hidden state:', error);
      alert(`Failed to ${hidden ? 'hide' : 'unhide'} account. Please try again.`);
    } finally {
      setHidingAccount(null);
    }
  };

  // Function to download transaction report
  const downloadTransactionReport = async (lawFirm) => {
    setDownloadingReport(prev => ({ ...prev, [lawFirm]: true }));
    try {
      const encodedLawFirm = encodeURIComponent(lawFirm);
      const response = await api.get(
        `/api/reports/transactions-by-law-firm/${encodedLawFirm}`,
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const date = new Date();
      const options = {
        timeZone: 'America/New_York',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
      };
      const formatter = new Intl.DateTimeFormat('en-US', options);
      const parts = formatter.formatToParts(date);
      const partValues = {};
      parts.forEach(part => { partValues[part.type] = part.value; });

      const currentTime = `${partValues.year}${partValues.month}${partValues.day}_${partValues.hour}${partValues.minute}${partValues.second}`;
      const filename = `${lawFirm.replace(/\s+/g, '_')}_transactions_${currentTime}.xlsx`;

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (err) {
      console.error(`Error downloading report for ${lawFirm}:`, err);
      alert(`Failed to download report for ${lawFirm}. Please try again.`);
    } finally {
      setDownloadingReport(prev => ({ ...prev, [lawFirm]: false }));
    }
  };

  // Function to sync transactions for a law firm
  const syncTransactionsForLawFirm = async (lawFirm) => {
    try {
      // Encode the law firm name for URL
      const encodedLawFirm = encodeURIComponent(lawFirm);
      
      // Call the law firm specific sync endpoint
      await api.post(`/api/utility/sync-transactions/${encodedLawFirm}`);
      
      // Show success message
      alert(`Transaction sync initiated for ${lawFirm}. This may take a few moments.`);
      
      // Wait a few seconds then refresh the data
      setTimeout(() => {
        fetchAccountData(currentPage, pageSize, searchTerm, showHidden);
      }, 5000); // Wait 5 seconds before refreshing
      
    } catch (err) {
      console.error(`Error syncing transactions for ${lawFirm}:`, err);
      if (err.response && err.response.status === 404) {
        alert(`No linked accounts found for ${lawFirm}.`);
      } else {
        alert(`Failed to sync transactions for ${lawFirm}. Please try again.`);
      }
    }
  };

  // Function to handle refresh button click
  const handleRefresh = async () => {
    if (refreshing) return; // Prevent multiple refresh operations
    
    setRefreshing(true);
    try {
      await fetchAccountData(currentPage, pageSize, searchTerm, showHidden);
    } catch (err) {
      console.error('Error refreshing data:', err);
    } finally {
      setRefreshing(false);
    }
  };

  // Function to fetch account data
  // `hidden` is threaded in explicitly by every caller rather than read from the
  // showHidden closure: the toggle has to fetch with the value it is about to set,
  // which React has not committed yet at that point.
  const fetchAccountData = async (page = currentPage, size = pageSize, search = searchTerm, hidden = showHidden, { initial = false } = {}) => {
    if (initial) {
      setLoading(true);
    } else {
      setTableLoading(true);
    }
    setError(null);

    try {
      const params = {
        page: page,
        page_size: size,
        include_hidden: hidden
      };
      
      if (search && search.trim()) {
        params.search = search.trim();
      }
      
      // Use the new dedicated endpoint to get accounts grouped by law firm with pagination
      const response = await api.get('/api/accounts/by-law-firm', { params });
      
      if (!response.data || !response.data.data || !response.data.pagination) {
        throw new Error('Unexpected response format from API');
      }
      
      const lawFirmAccountsArray = response.data.data;
      const paginationData = response.data.pagination;
      
      // Calculate summary stats from current page data
      const totalLinkedAccounts = lawFirmAccountsArray.reduce((sum, firm) => sum + firm.totalAccounts, 0);
      const totalBalance = lawFirmAccountsArray.reduce((sum, firm) => sum + firm.totalBalance, 0);
      
      setLawFirmAccounts(lawFirmAccountsArray);
      setPagination(paginationData);
      setCurrentPage(paginationData.current_page);
      setStats({
        totalLawFirms: paginationData.total_records, // Total law firms across all pages
        totalLinkedAccounts, // From current page
        totalBalance // From current page
      });
      
    } catch (err) {
      console.error('Error fetching account data:', err);
      setError('Failed to load account data. Please try again later.');
    } finally {
      if (initial) {
        setLoading(false);
      } else {
        setTableLoading(false);
      }
    }
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchAccountData(page, pageSize, searchTerm, showHidden);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
    fetchAccountData(1, size, searchTerm, showHidden);
  };

  // Passes the new value rather than reading showHidden back: setShowHidden has not
  // committed by the time this fetch fires. The firm list itself is unaffected by
  // include_hidden, so the current page stays put.
  const handleShowHiddenChange = (e) => {
    const nextShowHidden = e.target.checked;
    setShowHidden(nextShowHidden);
    fetchAccountData(currentPage, pageSize, searchTerm, nextShowHidden);
  };

  const handleSearchChange = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    setCurrentPage(1); // Reset to first page when searching

    // Debounce so we fire one request per pause in typing, not one per keystroke.
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      fetchAccountData(1, pageSize, newSearchTerm, showHidden);
    }, 300);
  };

  // Fetch accounts grouped by law firm
  useEffect(() => {
    fetchAccountData(1, pageSize, '', false, { initial: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: fetchAccountData is redefined each render
  }, []);

  // Cancel a pending debounced search if the component unmounts mid-typing, plus any
  // pending "saved" confirmation timer.
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      if (savedIndicatorRef.current) {
        clearTimeout(savedIndicatorRef.current);
      }
    };
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-600">Loading account data...</span>
      </div>
    );
  }

  // Error state: only bail out to a full-page error when there is nothing to show
  // and no active search. Otherwise fall through and render the inline banner so
  // the search box (and any way to clear it) stays mounted.
  if (error && lawFirmAccounts.length === 0 && !searchTerm) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-start">
        <AlertCircle className="w-5 h-5 mr-2 mt-0.5" />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-start">
          <AlertCircle className="w-5 h-5 mr-2 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      {/* Summary Stats Cards */}
      <div className="flex flex-col md:flex-row gap-4">
        {/* Total Law Firms Card */}
        <div className="bg-white rounded-3xl shadow-md border border-gray-100 p-5 flex-1">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-800">Law Firms</h2>
            <div className="p-2 bg-blue-50 rounded-full">
              <Building className="w-6 h-6 text-blue-500" />
            </div>
          </div>
          <p className="text-3xl font-bold mt-4 text-gray-900">{stats.totalLawFirms}</p>
          <p className="text-sm text-gray-500 mt-1">Total with linked accounts</p>
        </div>
        
        {/* Total Linked Accounts Card */}
        <div className="bg-white rounded-3xl shadow-md border border-gray-100 p-5 flex-1">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-800">Linked Accounts</h2>
            <div className="p-2 bg-green-50 rounded-full">
              <CreditCard className="w-6 h-6 text-green-500" />
            </div>
          </div>
          <p className="text-3xl font-bold mt-4 text-gray-900">{stats.totalLinkedAccounts}</p>
          <p className="text-sm text-gray-500 mt-1">Total accounts linked</p>
        </div>
        
        {/* Total Balance Card */}
        <div className="bg-white rounded-3xl shadow-md border border-gray-100 p-5 flex-1">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-800">Total Balance</h2>
            <div className="p-2 bg-yellow-50 rounded-full">
              <DollarSign className="w-6 h-6 text-yellow-500" />
            </div>
          </div>
          <p className="text-3xl font-bold mt-4 text-gray-900 font-mono tabular-nums">{formatCurrency(stats.totalBalance)}</p>
          <p className="text-sm text-gray-500 mt-1">Across all accounts</p>
        </div>
      </div>
      
      {/* Law Firm Accounts Table */}
      <div className="bg-white rounded-3xl shadow-md border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between space-y-3 sm:space-y-0">
          <div className="flex items-center">
            <h2 className="text-lg font-semibold text-gray-800">Linked Accounts by Law Firm</h2>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className={`ml-3 p-1 rounded-full ${refreshing ? 'bg-gray-100 text-gray-700' : 'bg-blue-50 text-blue-500 hover:bg-blue-100'}`}
              title="Refresh data"
            >
              <RotateCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            {/* Hidden accounts are excluded from the dashboard, reports, Balance
                Analytics and balance alerts, so staff need a way back to them. */}
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none whitespace-nowrap hover:text-gray-900 transition-colors duration-200">
              <input
                type="checkbox"
                checked={showHidden}
                onChange={handleShowHiddenChange}
                className="h-4 w-4 rounded border-gray-300 text-slate-600 focus:ring-slate-500"
              />
              Show hidden accounts
            </label>

            {/* Search Bar */}
            <div className="relative w-full sm:w-96">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search by law firm name"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-xl leading-5 bg-gray-50 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {searchTerm && (
                <button
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => {
                    if (searchDebounceRef.current) {
                      clearTimeout(searchDebounceRef.current);
                    }
                    setSearchTerm('');
                    setCurrentPage(1);
                    fetchAccountData(1, pageSize, '', showHidden);
                  }}
                >
                  <span className="text-gray-400 hover:text-gray-500">✕</span>
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          {searchTerm && (
            <div className="px-6 py-2 bg-gray-50 border-b border-gray-200 text-sm text-gray-500">
              Found {pagination.total_records} {pagination.total_records === 1 ? 'law firm' : 'law firms'} for "{searchTerm}"
            </div>
          )}
          {tableLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
              <span className="ml-3 text-gray-600">Loading account data...</span>
            </div>
          ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50/80">
              <tr>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                >
                  Law Firm
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                >
                  Linked Accounts
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                >
                  Current Balance
                </th>
                <th scope="col" className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Action
                </th>
                <th scope="col" className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {lawFirmAccounts.length > 0 ? (
                lawFirmAccounts.map((lawFirmData, index) => {
                  // Check if law firm has any broken accounts
                  const hasBrokenAccounts = lawFirmData.accounts.some(account => account.link_status === 'Broken');
                  // Check if ALL accounts are broken. The length guard matters: a firm
                  // whose only accounts are hidden comes back with an empty array, and
                  // [].every() is true -- it would render as a dead firm with Sync off.
                  const allAccountsBroken = lawFirmData.accounts.length > 0
                    && lawFirmData.accounts.every(account => account.link_status === 'Broken');
                  
                  return (
                  <React.Fragment key={lawFirmData.lawFirm}>
                    {/* Law Firm Row */}
                    <tr className={`hover:bg-blue-50/50 ${expandedFirms[lawFirmData.lawFirm] ? 'bg-gray-50' : ''} ${hasBrokenAccounts ? 'bg-red-50' : ''}`}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{lawFirmData.lawFirm}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        <span className="inline-flex items-center gap-2">
                          {lawFirmData.totalAccounts}
                          {/* Always shown, even with the toggle off: this count is the
                              only sign that some of the firm's accounts are silently
                              out of balance-alert monitoring. */}
                          {lawFirmData.hiddenAccounts > 0 && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700"
                              title={`${lawFirmData.hiddenAccounts} account${lawFirmData.hiddenAccounts === 1 ? '' : 's'} hidden by staff — excluded from these totals, Excel reports, Balance Analytics, and balance-threshold alerts. Turn on "Show hidden accounts" to see them.`}
                            >
                              <Archive className="w-3 h-3 mr-1" />
                              {lawFirmData.hiddenAccounts} hidden
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium font-mono tabular-nums text-gray-900">
                        {formatCurrency(lawFirmData.totalBalance)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <div className="flex space-x-2 justify-center">
                          <button
                            className={`inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded-full transition-all duration-200 ${
                              downloadingReport[lawFirmData.lawFirm]
                                ? 'bg-green-200 text-green-700 cursor-not-allowed'
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                            onClick={() => downloadTransactionReport(lawFirmData.lawFirm)}
                            disabled={downloadingReport[lawFirmData.lawFirm]}
                            title="Download financial report for this law firm"
                          >
                            {downloadingReport[lawFirmData.lawFirm] ? (
                              <>
                                <svg className="animate-spin -ml-0.5 mr-1 h-3 w-3 text-green-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Downloading...
                              </>
                            ) : (
                              <>
                                <Download className="w-3 h-3 mr-1" />
                                Report
                              </>
                            )}
                          </button>
                          <button
                            className={`inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded-full transition-all duration-200 ${
                              allAccountsBroken
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                                : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                            }`}
                            onClick={allAccountsBroken ? null : () => syncTransactionsForLawFirm(lawFirmData.lawFirm)}
                            disabled={allAccountsBroken}
                            title={allAccountsBroken ? "No linked accounts to sync" : "Synchronize latest account data"}
                          >
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Sync
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <button
                          onClick={() => toggleExpand(lawFirmData.lawFirm)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-full transition-all duration-200 bg-blue-100 text-blue-700 hover:bg-blue-200"
                        >
                          {expandedFirms[lawFirmData.lawFirm] ? (
                            <>
                              <ChevronUp className="w-3 h-3 mr-1" />
                              Collapse
                            </>
                          ) : (
                            <>
                              <ChevronDown className="w-3 h-3 mr-1" />
                              Expand
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                    
                    {/* Expanded Accounts Section */}
                    {expandedFirms[lawFirmData.lawFirm] && (
                      <tr>
                        <td colSpan="5" className="p-0">
                          <div className="border-t border-gray-200 bg-gray-50">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-100/80">
                                <tr>
                                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Account
                                  </th>
                                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Linked By
                                  </th>
                                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Account Name
                                  </th>
                                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Type
                                  </th>
                                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Current Balance
                                  </th>
                                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Last Activity
                                  </th>
                                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Account Use
                                  </th>
                                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {/* The edit form is split across three non-adjacent cells
                                    in a horizontally scrollable table, so the row tint and
                                    ring are the only thing binding them together as one
                                    operation. Editing outranks the status tints below
                                    because it is a transient, active user action. */}
                                {lawFirmData.accounts.map((account) => (
                                  <tr
                                    key={account.id}
                                    className={`hover:bg-blue-50/50 ${
                                      editingAccount === account.account_id
                                        ? 'bg-blue-50 ring-1 ring-inset ring-blue-300'
                                        : account.link_status === 'Broken'
                                        ? 'bg-red-100'
                                        : account.hidden_at
                                        ? 'bg-slate-100'
                                        : account.removed_at
                                        ? 'bg-amber-50'
                                        : ''
                                    }`}
                                  >
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      <div className="text-sm font-medium text-gray-900">{account.name}</div>
                                      <div className="text-xs text-gray-500">
                                        {account.mask ? `••••${account.mask}` : 'No mask available'}
                                      </div>
                                      {editingAccount === account.account_id ? (
                                        <input
                                          type="text"
                                          autoFocus
                                          value={nicknameDraft}
                                          maxLength={255}
                                          onChange={(e) => setNicknameDraft(e.target.value)}
                                          onKeyDown={handleEditKeyDown(account)}
                                          disabled={savingAccountId === account.account_id}
                                          aria-label={`Nickname for ${account.name}`}
                                          placeholder="Add a nickname"
                                          className="mt-1.5 w-44 px-2 py-1 text-xs text-gray-900 bg-white border border-gray-300 rounded-lg placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                                        />
                                      ) : account.nickname ? (
                                        <div
                                          className="mt-0.5 max-w-[11rem] truncate text-xs italic text-gray-600"
                                          title={account.nickname}
                                        >
                                          {account.nickname}
                                        </div>
                                      ) : null}
                                      {account.removed_at && (
                                        <span
                                          className="mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                                          title={`No longer returned by Plaid since ${formatRemovedAt(account.removed_at)}`}
                                        >
                                          <AlertCircle className="w-3 h-3 mr-1" />
                                          Removed at bank
                                        </span>
                                      )}
                                      {account.hidden_at && (
                                        <span
                                          className={`mt-1 ${account.removed_at ? 'ml-1' : ''} inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-200 text-slate-700`}
                                          title="Set aside by staff — excluded from the dashboard, Excel reports, Balance Analytics, and balance-threshold alerts until unhidden."
                                        >
                                          <Archive className="w-3 h-3 mr-1" />
                                          Hidden
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      <div className="text-sm text-gray-900">
                                        {account.customerInfo.firstName} {account.customerInfo.lastName}
                                      </div>
                                      <div className="text-xs text-gray-500">{account.customerInfo.email}</div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      <div className="text-sm font-medium text-gray-900">
                                        {account.official_name || 'N/A'}
                                      </div>
                                      {account.institution_name && (
                                        <div className="text-xs text-gray-500">
                                          {account.institution_name}
                                        </div>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                        {account.type}{account.subtype ? ` - ${account.subtype}` : ''}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium font-mono tabular-nums text-gray-900">
                                      {formatCurrency(account.current_balance)}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                      {formatLastActivity(account.last_activity)}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      {editingAccount === account.account_id ? (
                                        <div className="space-y-1.5">
                                          <select
                                            value={accountUseDraft}
                                            onChange={(e) => setAccountUseDraft(e.target.value)}
                                            onKeyDown={handleEditKeyDown(account)}
                                            disabled={savingAccountId === account.account_id}
                                            aria-label={`Account use for ${account.name}`}
                                            className="px-2 py-1 text-xs text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                                          >
                                            <option value="iolta">IOLTA</option>
                                            <option value="operating">Operating</option>
                                            <option value="">None</option>
                                          </select>
                                          <div>
                                            <label className="block text-xs text-gray-500 mb-0.5" htmlFor={`low-balance-${account.account_id}`}>
                                              Low-balance threshold
                                            </label>
                                            <div className="relative w-32">
                                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-500">$</span>
                                              <input
                                                id={`low-balance-${account.account_id}`}
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={thresholdDraft}
                                                onChange={(e) => {
                                                  setThresholdDraft(e.target.value);
                                                  setThresholdBadInput(e.target.validity.badInput);
                                                }}
                                                onKeyDown={handleEditKeyDown(account)}
                                                disabled={accountUseDraft !== 'operating' || savingAccountId === account.account_id}
                                                placeholder="Default"
                                                title={accountUseDraft === 'operating'
                                                  ? 'Blank uses each subscription\'s default. An override applies to every subscriber.'
                                                  : 'Set the account use to Operating to set a low-balance threshold.'}
                                                className="w-full pl-5 pr-2 py-1 text-xs text-gray-900 bg-white border border-gray-300 rounded-lg placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="flex items-center space-x-2">
                                          {account.is_iolta || account.is_operating ? (
                                            <span className="px-2 py-0.5 inline-flex text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                                              {account.is_iolta ? 'IOLTA' : 'Operating'}
                                            </span>
                                          ) : (
                                            <span className="text-sm text-gray-500" title="No account use set"><span aria-hidden="true">—</span><span className="sr-only">None</span></span>
                                          )}
                                          {account.is_operating && account.low_balance_threshold != null && (() => {
                                            const pausedReason = lowBalancePausedReason(account);
                                            return (
                                              <span
                                                className={`text-xs ${pausedReason ? 'text-gray-500 italic' : 'text-gray-600'}`}
                                                title={[
                                                  'Low-balance threshold override, applies to every subscriber.',
                                                  pausedReason ? `Paused (${pausedReason}): the daily digest skips this account.` : null,
                                                  account.low_balance_threshold_updated_by ? `Set by ${account.low_balance_threshold_updated_by}` : null,
                                                  account.low_balance_threshold_updated_at ? new Date(account.low_balance_threshold_updated_at).toLocaleString() : null
                                                ].filter(Boolean).join(' ')}
                                              >
                                                Low-balance: <span className="font-mono tabular-nums">{formatCurrency(account.low_balance_threshold)}</span>
                                                {pausedReason && ` · paused (${pausedReason})`}
                                              </span>
                                            );
                                          })()}
                                        </div>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      {editingAccount === account.account_id ? (
                                        <div className="flex items-center space-x-1.5">
                                          <button
                                            onClick={() => saveAccountEdits(account)}
                                            disabled={savingAccountId === account.account_id}
                                            className={`inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded-full transition-all duration-200 ${
                                              savingAccountId === account.account_id
                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                            }`}
                                          >
                                            {savingAccountId === account.account_id ? 'Saving...' : 'Save'}
                                          </button>
                                          <button
                                            onClick={cancelEditingAccount}
                                            disabled={savingAccountId === account.account_id}
                                            className="text-xs text-gray-500 hover:text-gray-700 transition-colors duration-200 disabled:opacity-50"
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      ) : (
                                        <div className="flex items-center space-x-2">
                                          {/* A text pill, not a check icon, so the confirmation
                                              is not read as a data flag. */}
                                          {savedAccountId === account.account_id ? (
                                            <span
                                              className="inline-flex items-center -my-0.5 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700"
                                              title="Changes saved"
                                              aria-live="polite"
                                            >
                                              Saved
                                            </span>
                                          ) : (
                                            <button
                                              onClick={() => startEditingAccount(account)}
                                              disabled={isRowBusy(account.account_id)}
                                              title="Edit nickname, account use and low-balance threshold"
                                              aria-label={`Edit nickname, account use and low-balance threshold for ${account.name}`}
                                              // p-1 -m-1: grows the hit target to the 24px
                                              // minimum without shifting the buttons beside it.
                                              className={`p-1 -m-1 rounded-full transition-colors duration-200 ${
                                                isRowBusy(account.account_id)
                                                  ? 'text-gray-400 cursor-not-allowed'
                                                  : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                                              }`}
                                            >
                                              <Pencil className="h-4 w-4" />
                                            </button>
                                          )}
                                          {account.hidden_at ? (
                                            <button
                                              onClick={() => setAccountHidden(account.account_id, account.name, false)}
                                              disabled={isRowBusy(account.account_id)}
                                              title={`Unhide — restore this account to the dashboard, reports, Balance Analytics, and balance-threshold alerts.${
                                                account.removed_at
                                                  ? ' This account is also flagged as removed at the bank — dismissing becomes available after unhiding.'
                                                  : ''
                                              }`}
                                              className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full border transition-all duration-200 ${
                                                isRowBusy(account.account_id)
                                                  ? 'bg-gray-100 text-gray-500 border-transparent cursor-not-allowed'
                                                  : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                                              }`}
                                            >
                                              <ArchiveRestore className="w-3 h-3 mr-1" />
                                              {hidingAccount === account.account_id ? 'Unhiding...' : 'Unhide'}
                                            </button>
                                          ) : (
                                            <button
                                              onClick={() => setHideConfirmAccount(account)}
                                              disabled={isRowBusy(account.account_id)}
                                              title="Hide — reversible, for accounts you know are still active (a duplicate or a closed-out matter). Excluded from the dashboard, reports, Balance Analytics, and balance alerts until you unhide it."
                                              className={`inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded-full transition-all duration-200 ${
                                                isRowBusy(account.account_id)
                                                  ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                              }`}
                                            >
                                              <Archive className="w-3 h-3 mr-1" />
                                              {hidingAccount === account.account_id ? 'Hiding...' : 'Hide'}
                                            </button>
                                          )}
                                          {/* Dismiss is a no-op server-side on a hidden
                                              account (it must be unhidden first), so it
                                              is not offered on one. */}
                                          {account.removed_at && !account.hidden_at && (
                                            <button
                                              onClick={() => dismissAccount(account.account_id, account.name)}
                                              disabled={isRowBusy(account.account_id)}
                                              title="Permanently remove this account from the dashboard and reports"
                                              className={`inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded-full transition-all duration-200 ${
                                                isRowBusy(account.account_id)
                                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                  : 'bg-rose-100 text-rose-700 hover:bg-rose-200'
                                              }`}
                                            >
                                              <Trash2 className="w-3 h-3 mr-1" />
                                              {dismissingAccount === account.account_id ? 'Dismissing...' : 'Dismiss'}
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
                })
              ) : (
                <tr>
                  <td colSpan="5" className="px-4 py-3 text-center text-sm text-gray-500">
                    {searchTerm ? 'No law firms match your search criteria' : 'No linked accounts found. Use the Request Access page to send access links to customers.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          )}
        </div>
        
        {/* Pagination Controls */}
        {!loading && !error && lawFirmAccounts.length > 0 && (
          <div className="px-6 py-4">
            <Pagination
              pagination={pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSize={pageSize}
              pageSizeOptions={[10, 15, 20]}
              showInfo={true}
            />
          </div>
        )}
      </div>

      {/* Hide confirmation dialog */}
      {hideConfirmAccount && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setHideConfirmAccount(null)}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hide-confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="hide-confirm-title" className="text-lg font-semibold text-gray-900">
              Hide this account?
            </h2>
            <p className="mt-3 text-sm text-gray-600">
              {`"${hideConfirmAccount.name}" will be excluded from the dashboard, Excel reports, Balance Analytics, and balance-threshold alert emails until you unhide it via the "Show hidden accounts" toggle.`}
            </p>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setHideConfirmAccount(null)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-full border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setAccountHidden(hideConfirmAccount.account_id, hideConfirmAccount.name, true);
                  setHideConfirmAccount(null);
                }}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all duration-200"
              >
                <Archive className="w-3.5 h-3.5 mr-1.5" />
                Hide
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSummary;